import { BASE_URL } from "@src/utils";
import axios, { ServerResponse } from "../axios";

export interface IPartner {
  uuid: string;
  name: string;
  bizName: string;
  type: number;
  typeName: string;
  csState: number;
  tel: string;
  address: string;
  address2: string;
  region: string;
  logoUrl: string;
  coverUrl: string;
  profile: string;
  profileUrl: string;
  rating: number;
  reviewCount: number;
  reviewCnt: number;
  reviewLatestCount: number;
  modTsp: string;
  tags: {
    type: number;
    typeName: string;
    name: string;
    json: any;
  }[];
  benefits: any[];
  memoContent: string;
  regTsp: string;

  // 프론트에서 추가된 필드 (프론트 전용 -> 모두 찜한 상태이기 때문에 isLiked 자체가 백 상에서는 무의미하나 프론트에서는 해당 값이 필요해서 추가)
  isLiked?: boolean;
  bookingState: 1 | 0 | null;
}
export interface ISlot {
  partnerUuid: string;
  individualHallId: number;
  individualRepresentativeImageUrl: string;
  weddingDayId: number;
  name: string;
  rentalFeeOfIndividualHall: number;
  flowerFeeOfIndividualHall: number;
  adultDrinkFeeOfIndividualHall: number;
  childDrinkFeeOfIndividualHall: number;
  includedDrinkFeeOfIndividualHall: boolean;
  includedAlcoholFeeOfIndividualHall: boolean;
  includedServiceFeeOfIndividualHall: boolean;
  includedVatOfIndividualHall: boolean;
  additionalProductsJsonOfIndividualHall: string;
  rentalFeeOfWeddingDay: number;
  flowerFeeOfWeddingDay: number;
  adultFeeOfWeddingDay: number;
  childFeeOfWeddingDay: number;
  beverageFeeOfWeddingDay: number;
  beverageQuantityOfWeddingDay: number;
  alcoholFeeOfWeddingDay: number;
  alcoholQuantityOfWeddingDay: number;
  giftFeeOfWeddingDay: number;
  giftQuantityOfWeddingDay: number;
  weddingDate: string;
  weddingTime: string;
  guarantee: number;
  isLiked: boolean;
  likeTsp: string;
  totalAmount: number;
  finalAmount: number;
  finalDiscountPercentage: number;
}

export interface ISlotOrder {
  code: string;
  name: string;
}

export interface IPartnerCategory {
  id: number;
  type: number;
  name: string;
  count: number;
}

export interface IPortfolio {
  id: number;
  title: string;
  mainImageUrl: string;
  plannerName: string;
  partnerUuid: string;
  // 프론트에서 추가된 필드 (프론트 전용 -> 모두 찜한 상태이기 때문에 isLiked 자체가 백 상에서는 무의미하나 프론트에서는 해당 값이 필요해서 추가)
  isLiked?: boolean;
}

/**
 *
 * 찜(관심) 등록
 * 웨딩홀상품(슬롯), 플래너, 포트폴리오, 업체
 */

// 관심 웨딩홀상품(슬롯) 등록
export const postLikeSlot = (weddingDayId: number) => {
  return axios.post<ServerResponse>(`${BASE_URL}/v3/like/hall/${weddingDayId}`);
};

// 관심 플래너 등록
export const postLikePlanner = (uuid: string, name: string) => {
  const formData = new FormData();
  formData.append("partnerUuid", uuid);
  formData.append("partnerName", name);
  formData.append("state", "1");
  return axios.post(`${BASE_URL}/v3/like/planner`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// 관심 포트폴리오 등록
export const postLikePortfolio = (portfolioId: string) => {
  return axios.post<ServerResponse>(`${BASE_URL}/v3/like/portfolio/${portfolioId}`);
};

// 관심 업체 등록
export const postLikePartner = (uuid: string, name: string) => {
  const formData = new FormData();
  formData.append("partnerUuid", uuid);
  formData.append("partnerName", name);
  formData.append("state", "1");
  return axios.post(`${BASE_URL}/v3/like/partner`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 *
 * 찜(관심) 삭제
 * 웨딩홀상품(슬롯), 플래너, 포트폴리오, 업체
 */

// 관심 웨딩홀상품(슬롯) 삭제
export const deleteLikeSlot = (weddingDayId: number) => {
  return axios.delete<ServerResponse>(`${BASE_URL}/v3/like/hall/${weddingDayId}`);
};

// 관심 플래너 삭제
export const deleteLikePlanner = (uuid: string, name: string) => {
  return axios.delete<ServerResponse>(
    `${BASE_URL}/v3/like/partner?partnerUuid=${uuid}&partnerName=${name}&state=0`,
  );
};

// 관심 포트폴리오 삭제
export const deleteLikePortfolio = (portfolioId: string) => {
  return axios.delete<ServerResponse>(`${BASE_URL}/v3/like/portfolio/${portfolioId}`);
};

// 관심 업체 삭제
export const deleteLikePartner = (uuid: string, name: string) => {
  return axios.delete<ServerResponse>(
    `${BASE_URL}/v3/like/partner?partnerUuid=${uuid}&partnerName=${name}&state=0`,
  );
};

/**
 *
 * 찜(관심) 카테고리 조회
 * 업체
 */

// 관심 업체 카테고리 조회
export const getLikePartnerCategories = async () => {
  const data = await axios.get<{ item: IPartnerCategory[] }>(
    `${BASE_URL}/v3/like/partner/categories`,
  );
  // 웨딩홀 타입(2)은 리스트에서 제거하고, 전체 카운트에서도 그만큼 제거함
  const list = data.data.item;
  const filteredData = list.filter((item) => item.type !== 2);
  const weddinghallType = list.find((item) => item.type === 2);
  const totalCount = filteredData.find((item) => item.type === 0)?.count || 0;

  const filteredItem = filteredData.map((item) => ({
    ...item,
    count: item.type === 0 ? totalCount - (weddinghallType?.count ?? 0) : item.count,
  }));

  return {
    ...data,
    data: {
      ...data.data,
      item: filteredItem,
    },
  };
};

/**
 *
 * 찜(관심) 필터 조회
 * 웨딩홀상품(슬롯)
 */

// 관심 웨딩홀상품(슬롯) 필터
export const getLikeSlotOrders = () => {
  return axios.get<{ item: ISlotOrder[] }>(`${BASE_URL}/v3/like/hall/orders`);
};

/**
 *
 * 찜(관심) 목록 조회
 * 웨딩홀상품(슬롯), 플래너, 포트폴리오, 업체
 */

// 관심 웨딩홀상품(슬롯) 조회
export const getLikeSlot = ({
  orderCode,
  pageParam = 1,
  fetchSize = 20,
}: {
  orderCode?: string;
  pageParam?: number;
  fetchSize?: number;
}) => {
  const params = new URLSearchParams();
  params.append("page", pageParam.toString());
  params.append("fetchSize", fetchSize.toString());
  if (orderCode) params.append("orderCode", orderCode);
  return axios.get<ServerResponse<ISlot[]>>(`${BASE_URL}/v3/like/halls?${params.toString()}`);
};

// 관심 플래너 조회
export const getLikePlanners = ({
  fetchSize,
  lastRegTsp,
}: {
  fetchSize: number;
  lastRegTsp?: Date;
}) => {
  const p = lastRegTsp
    ? `?fetchSize=${fetchSize}&lastRegTsp=${lastRegTsp}`
    : `?fetchSize=${fetchSize}`;

  return axios.get<ServerResponse<IPartner[]>>(`${BASE_URL}/v3/like/planners${p}`).then((res) => {
    const addIsLiked = res.data.item.map((item: IPartner) => {
      return {
        ...item,
        isLiked: true,
      };
    });
    const res2 = {
      ...res,
      data: {
        ...res.data,
        item: addIsLiked,
      },
    };
    return res2;
  });
};

// 관심 포트폴리오 조회
export const getLikePortfolio = ({
  fetchSize,
  lastRegTsp,
}: {
  fetchSize: number;
  lastRegTsp?: Date;
}) => {
  const p = lastRegTsp
    ? `?fetchSize=${fetchSize}&lastRegTsp=${lastRegTsp}`
    : `?fetchSize=${fetchSize}`;

  return axios
    .get<ServerResponse<IPortfolio[]>>(`${BASE_URL}/v3/like/portfolios${p}`)
    .then((res) => {
      const addIsLiked = res.data.item.map((item: IPortfolio) => {
        return {
          ...item,
          isLiked: true,
        };
      });
      const res2 = {
        ...res,
        data: {
          ...res.data,
          item: addIsLiked,
        },
      };
      return res2;
    });
};

// 관심 업체 조회
export const getLikePartners = ({
  fetchSize,
  type,
  lastRegTsp,
}: {
  fetchSize: number;
  type?: number;
  lastRegTsp?: Date;
}) => {
  let p = `?fetchSize=${fetchSize}&type=${type}`;
  if (lastRegTsp) {
    p += `&lastRegTsp=${lastRegTsp}`;
  }
  return axios.get<ServerResponse<IPartner[]>>(`${BASE_URL}/v3/like/partners${p}`).then((res) => {
    const addIsLiked = res.data.item.map((item: IPartner) => {
      return {
        ...item,
        isLiked: true,
      };
    });
    const res2 = {
      ...res,
      data: {
        ...res.data,
        item: addIsLiked,
      },
    };
    return res2;
  });
};
